/**
 * Pure layout + formatting helpers for the Orchestration Status DAG (PWA-2150).
 *
 * The graph is laid out entirely from each step's `dependsOn` (longest-path
 * layering) so the topology is never hardcoded -- the backend step graph can
 * change and the UI follows it.
 */

import { OrchestrationStepState } from "../types/orchestration";

export interface NodePosition {
  x: number;
  y: number;
}

export interface GraphLayout {
  positions: Record<string, NodePosition>;
  /** Layer index per step id. */
  layers: Record<string, number>;
  width: number;
  height: number;
  /** Number of nodes in the tallest layer. */
  maxRows: number;
}

export interface LayoutOptions {
  nodeWidth: number;
  nodeHeight: number;
  colGap: number;
  rowGap: number;
  pad: number;
}

export const DEFAULT_LAYOUT: LayoutOptions = {
  nodeWidth: 196,
  nodeHeight: 96,
  colGap: 90,
  rowGap: 26,
  pad: 40,
};

/**
 * Assign each step a layer index = longest dependency path from any root.
 * - Unknown `dependsOn` ids (not present in `steps`) are ignored.
 * - Cycles are made safe via a visited-guard: a back-reference contributes the
 *   best depth seen so far instead of recursing forever.
 */
export function computeLayers(
  steps: OrchestrationStepState[]
): Record<string, number> {
  const byId: Record<string, OrchestrationStepState> = {};
  steps.forEach((s) => {
    byId[s.id] = s;
  });

  const layer: Record<string, number> = {};
  const inProgress: Record<string, boolean> = {};

  function depth(id: string): number {
    if (layer[id] !== undefined) return layer[id];
    if (inProgress[id]) return 0; // cycle guard
    const node = byId[id];
    if (!node || !node.dependsOn || node.dependsOn.length === 0) {
      layer[id] = 0;
      return 0;
    }
    inProgress[id] = true;
    let max = 0;
    node.dependsOn.forEach((dep) => {
      if (byId[dep]) max = Math.max(max, depth(dep) + 1);
    });
    inProgress[id] = false;
    layer[id] = max;
    return max;
  }

  steps.forEach((s) => depth(s.id));
  return layer;
}

/**
 * Group steps by layer, stack within a layer, and return absolute positions.
 * Each layer's column is vertically centered against the tallest layer.
 */
export function layoutGraph(
  steps: OrchestrationStepState[],
  opts: LayoutOptions = DEFAULT_LAYOUT
): GraphLayout {
  const layers = computeLayers(steps);
  const { nodeWidth, nodeHeight, colGap, rowGap, pad } = opts;

  const byLayer: Record<number, OrchestrationStepState[]> = {};
  steps.forEach((s) => {
    const L = layers[s.id] ?? 0;
    (byLayer[L] ||= []).push(s);
  });

  const layerIndices = Object.keys(byLayer).map(Number);
  const maxLayer = layerIndices.length ? Math.max(...layerIndices) : 0;
  const maxRows = layerIndices.length
    ? Math.max(...layerIndices.map((L) => byLayer[L].length))
    : 0;

  const totalH = maxRows * nodeHeight + Math.max(0, maxRows - 1) * rowGap;

  const positions: Record<string, NodePosition> = {};
  for (let L = 0; L <= maxLayer; L++) {
    const col = byLayer[L] || [];
    const colH = col.length * nodeHeight + Math.max(0, col.length - 1) * rowGap;
    const startY = pad + (totalH - colH) / 2;
    col.forEach((node, i) => {
      positions[node.id] = {
        x: pad + L * (nodeWidth + colGap),
        y: startY + i * (nodeHeight + rowGap),
      };
    });
  }

  const width = pad * 2 + (maxLayer + 1) * nodeWidth + maxLayer * colGap;
  const height = pad * 2 + totalH;

  return { positions, layers, width, height, maxRows };
}

/** Format a duration in ms as "1.2s" (<60s) or "10m 02s". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
    if (totalSeconds < 10) {
      // one decimal under 10s (manual to avoid ad-hoc toFixed formatting)
      const tenths = Math.round(totalSeconds * 10);
      return `${Math.floor(tenths / 10)}.${tenths % 10}s`;
    }
    return `${Math.round(totalSeconds)}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

/**
 * Elapsed time from an ISO start to `nowMs`, as "MM:SS" or "H:MM:SS".
 * Returns "—" for a missing/invalid start, and clamps negatives to zero.
 */
export function formatElapsed(
  startIso: string | null | undefined,
  nowMs: number
): string {
  if (!startIso) return "—";
  const start = Date.parse(startIso);
  if (Number.isNaN(start)) return "—";
  const elapsedSec = Math.max(0, Math.floor((nowMs - start) / 1000));
  const h = Math.floor(elapsedSec / 3600);
  const m = Math.floor((elapsedSec % 3600) / 60);
  const s = elapsedSec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * ISO of the 30-minute floor of `nowMs`, in UTC -- the slot the orchestrator
 * is expected to run for at that instant. e.g. 12:01 and 12:29 -> 12:00;
 * 12:30 and 12:40 -> 12:30.
 */
export function floorToSlotUtc(nowMs: number): string {
  const d = new Date(nowMs);
  d.setUTCMinutes(d.getUTCMinutes() < 30 ? 0 : 30, 0, 0);
  return d.toISOString();
}

/**
 * True when the newest recorded slot is older than the expected current slot
 * -- i.e. the current 30-minute slot's orchestration has not been recorded yet.
 * Returns false when there is no latest slot or its time is unparseable.
 */
export function isExpectedSlotPending(
  latestSlotUtc: string | null | undefined,
  nowMs: number
): boolean {
  if (!latestSlotUtc) return false;
  const latest = Date.parse(latestSlotUtc);
  if (Number.isNaN(latest)) return false;
  return latest < Date.parse(floorToSlotUtc(nowMs));
}

/** Grace period (minutes) after a slot's start before the run is "late". */
export const ONCALL_LATE_THRESHOLD_MIN = 5;

/**
 * True when the current slot is still pending AND at least `thresholdMinutes`
 * have elapsed since the slot's scheduled start -- the orchestration job is late
 * to start and oncall should be contacted.
 */
export function isExpectedSlotLate(
  latestSlotUtc: string | null | undefined,
  nowMs: number,
  thresholdMinutes: number = ONCALL_LATE_THRESHOLD_MIN
): boolean {
  if (!isExpectedSlotPending(latestSlotUtc, nowMs)) return false;
  const slotStart = Date.parse(floorToSlotUtc(nowMs));
  return nowMs - slotStart >= thresholdMinutes * 60000;
}

/** Format an ISO slot time as "Jun 04, 00:30 UTC". */
export function formatSlotLabel(slotUtc: string): string {
  const d = new Date(slotUtc);
  if (Number.isNaN(d.getTime())) return slotUtc;
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const day = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${month} ${day}, ${hh}:${mm} UTC`;
}
